import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FileText, Bookmark, Loader2 } from 'lucide-react';

const PERSIST_KEY = 'creatorflow_script';
const PERSIST_MINUTES = 5;

const ScriptBuilder = () => {
  const { api, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState(() => {
    try {
      const saved = localStorage.getItem(PERSIST_KEY);
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        if (Date.now() - timestamp < PERSIST_MINUTES * 60 * 1000) return data;
        localStorage.removeItem(PERSIST_KEY);
      }
    } catch (e) {}
    return null;
  });
  const [form, setForm] = useState({
    topic: '',
    style: 'educational',
    hook: ''
  });

  // Persist script when it changes
  useEffect(() => {
    if (script) {
      localStorage.setItem(PERSIST_KEY, JSON.stringify({ data: script, timestamp: Date.now() }));
    }
  }, [script]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.topic) {
      toast.error('Please enter a topic');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/scripts/generate', form);
      setScript(res.data);
      toast.success(`Script generated! (${res.data.creditsUsed} credits used)`);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate script');
    } finally {
      setLoading(false);
    }
  };

  const saveScript = async () => {
    if (!script) return;
    try {
      await api.post('/saved', {
        type: 'script',
        title: form.topic,
        content: script
      });
      toast.success('Script saved to library!');
    } catch (err) {
      toast.error('Failed to save script');
    }
  };

  return (
    <div className="space-y-6" data-testid="script-builder">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Script Builder
          </h1>
          <p className="text-slate-400 text-lg">
            Turn ideas into full video scripts instantly.
          </p>
        </div>
        {script && (
          <Button
            onClick={saveScript}
            variant="outline"
            className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
            data-testid="save-script-btn"
          >
            <Bookmark className="w-4 h-4 mr-2" />
            Save to Library
          </Button>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <Card className="bg-slate-800/30 border-slate-700/50 h-fit">
          <CardContent className="p-6">
            <form onSubmit={handleGenerate} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-white font-medium">Video Topic</Label>
                <Input
                  data-testid="script-topic"
                  placeholder="e.g. 5 tips for better sleep"
                  value={form.topic}
                  onChange={(e) => setForm({...form, topic: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white font-medium">Script Style</Label>
                <Select 
                  value={form.style} 
                  onValueChange={(v) => setForm({...form, style: v})}
                >
                  <SelectTrigger data-testid="script-style" className="bg-slate-900/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="educational">Educational / How-to</SelectItem>
                    <SelectItem value="storytelling">Storytelling</SelectItem>
                    <SelectItem value="motivational">Motivational</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white font-medium">Hook (Optional)</Label>
                <Textarea
                  data-testid="script-hook"
                  placeholder="Paste a hook you generated earlier..."
                  value={form.hook}
                  onChange={(e) => setForm({...form, hook: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 min-h-[120px] resize-y"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 bg-violet-500 hover:bg-violet-600 text-white text-lg font-medium"
                data-testid="generate-script-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5 mr-2" />
                    Generate Script
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm">Uses 5 credits</p>
            </form>
          </CardContent>
        </Card>

        {/* Right Column - Results */}
        <Card className="bg-slate-800/30 border-slate-700/50 min-h-[500px]">
          <CardContent className="p-6 h-full">
            {!script ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <FileText className="w-16 h-16 text-slate-600 mb-4" />
                <p className="text-slate-400 text-lg">
                  Waiting to generate your masterpiece...
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Script Title */}
                <h3 className="text-lg font-bold text-white">
                  {form.topic} (30 Seconds)
                </h3>

                {/* Hook Section */}
                {script.beats?.find(b => b.name?.toLowerCase().includes('hook')) && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border-l-4 border-violet-500">
                    <p className="text-violet-400 text-sm font-medium mb-1">HOOK</p>
                    <p className="text-white">
                      {script.beats.find(b => b.name?.toLowerCase().includes('hook'))?.content}
                    </p>
                  </div>
                )}

                {/* Beats */}
                {script.beats?.filter(b => !b.name?.toLowerCase().includes('hook') && !b.name?.toLowerCase().includes('cta')).map((beat, idx) => (
                  <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border-l-4 border-slate-700">
                    <p className="text-slate-500 text-sm font-medium mb-1">BEAT {idx + 1}</p>
                    <p className="text-slate-300">{beat.content}</p>
                  </div>
                ))}

                {/* CTA Section */}
                {script.beats?.find(b => b.name?.toLowerCase().includes('cta')) && (
                  <div className="bg-cyan-900/30 rounded-lg p-4 border-l-4 border-cyan-500">
                    <p className="text-cyan-400 text-sm font-medium mb-1">CALL TO ACTION</p>
                    <p className="text-white">
                      {script.beats.find(b => b.name?.toLowerCase().includes('cta'))?.content}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ScriptBuilder;
